/**
 * G42 (Paul, 2026-10-21; the phone's rule, mobile #1972): a "Review our terms" prompt left
 * unanswered is not "Not now". /auth/sso-complete records "consent pending" for the new
 * account before it shows the prompt; until the person picks Accept or Not now, every
 * signed-in page shows the prompt again instead of the page (PendingConsentRoutes). The
 * outcomes don't change: both choices go to Pick your plan, and the bank-connect gate
 * still asks before any bank data moves.
 *
 * The flag is per account (keyed by the Clerk user id), so a different account on the same
 * browser never sees it. It lives in localStorage so it survives closing the tab. Every
 * access is guarded: with storage blocked, sign-in behaves as it did before G42.
 */
import { NEW_ACCOUNT_DESTINATION } from "@/lib/ssoLanding";

const PREFIX = "fomo-fi:consent-pending:";

type DeviceStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function deviceStorage(): DeviceStore | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/** Recorded before the prompt shows, so no route ever sees the account as not owing consent. */
export function markConsentPending(
  userId: string,
  storage: DeviceStore | undefined = deviceStorage(),
): void {
  try {
    storage?.setItem(PREFIX + userId, "1");
  } catch {
    // Storage blocked: the bank-connect consent gate still asks before any data moves.
  }
}

export function isConsentPending(
  userId: string | null | undefined,
  storage: DeviceStore | undefined = deviceStorage(),
): boolean {
  if (!userId) return false;
  try {
    return storage?.getItem(PREFIX + userId) === "1";
  } catch {
    return false;
  }
}

export function clearConsentPending(
  userId: string | null | undefined,
  storage: DeviceStore | undefined = deviceStorage(),
): void {
  if (!userId) return;
  try {
    storage?.removeItem(PREFIX + userId);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/**
 * Pages the prompt never covers: sign-in and sign-up (sso-complete shows its own prompt),
 * the Terms and Privacy pages the prompt links to (they open in a new tab and must stay
 * readable), Help, and the waitlist links sent emails carry.
 */
export const CONSENT_HOST_EXEMPT_PREFIXES = [
  "/auth",
  "/legal",
  "/terms",
  "/privacy",
  "/help",
  "/waitlist",
] as const;

export interface ConsentHostInput {
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string | null | undefined;
  pathname: string;
  storage?: DeviceStore;
}

/** "hold": show the prompt instead of the page. "pass": render the page. */
export function consentHostDecision({
  isLoaded,
  isSignedIn,
  userId,
  pathname,
  storage = deviceStorage(),
}: ConsentHostInput): "hold" | "pass" {
  if (!isLoaded || !isSignedIn || !userId) return "pass";
  const exempt = CONSENT_HOST_EXEMPT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  if (exempt) return "pass";
  return isConsentPending(userId, storage) ? "hold" : "pass";
}

/**
 * Sign-out clears that account's flag only, once the session has actually ended (the
 * signed-in user id went from someone to no one). Switching accounts clears nothing: the
 * other account's flag is untouched, and the new account reads only its own key.
 */
export function clearOnSignOut(
  previousUserId: string | null | undefined,
  nextUserId: string | null | undefined,
  storage: DeviceStore | undefined = deviceStorage(),
): void {
  if (previousUserId && !nextUserId) clearConsentPending(previousUserId, storage);
}

/**
 * For Clerk's `addListener`, which reports the session change synchronously as sign-out
 * ends it. Clerk then leaves the page with a full load, so a React effect waiting for the
 * next render never sees the account go (measured on the local stack).
 */
export function signOutWatcher(storage: DeviceStore | undefined = deviceStorage()) {
  let last: string | null = null;
  return ({ user }: { user?: { id: string } | null }) => {
    const next = user?.id ?? null;
    clearOnSignOut(last, next, storage);
    last = next;
  };
}

export interface AnswerDeps {
  userId: string | null | undefined;
  navigate: (to: string, options: { replace: boolean }) => void;
  storage?: DeviceStore;
}

/** Not now: the person chose, so the prompt is answered. Pick your plan, as before. */
export function answerNotNow({ userId, navigate, storage = deviceStorage() }: AnswerDeps): void {
  clearConsentPending(userId, storage);
  navigate(NEW_ACCOUNT_DESTINATION, { replace: true });
}

/**
 * Accept: record it first. The flag clears only once the acceptance is saved; a failed
 * save keeps the flag and the prompt (the caller shows the failure line).
 */
export async function answerAccept({
  userId,
  navigate,
  storage = deviceStorage(),
  accept,
}: AnswerDeps & { accept: () => Promise<unknown> }): Promise<void> {
  await accept();
  clearConsentPending(userId, storage);
  navigate(NEW_ACCOUNT_DESTINATION, { replace: true });
}

/**
 * D3 (mobile #1972, found on the phone): on the held prompt, an answer goes to Pick your
 * plan while the hold is still on, and the flag clears only once that page is the current
 * route (`releaseOnArrival`). Clearing first released the hold on the route the person was
 * on, and the setup gate there sent a new account into setup instead of Pick your plan.
 * Not now, Accept (after its save) and a prompt that cannot render all leave this way.
 */
export function leaveHeldPrompt(navigate: AnswerDeps["navigate"]): void {
  navigate(NEW_ACCOUNT_DESTINATION, { replace: true });
}

/** Accept on the held prompt: save, then leave; a failed save stays on the prompt. */
export async function acceptHeldPrompt({
  navigate,
  accept,
}: Pick<AnswerDeps, "navigate"> & { accept: () => Promise<unknown> }): Promise<void> {
  await accept();
  leaveHeldPrompt(navigate);
}

/** True once an answered prompt has reached Pick your plan; the flag is then cleared. */
export function releaseOnArrival({
  userId,
  pathname,
  answered,
  storage = deviceStorage(),
}: {
  userId: string | null | undefined;
  pathname: string;
  answered: boolean;
  storage?: DeviceStore;
}): boolean {
  if (!answered || pathname !== NEW_ACCOUNT_DESTINATION) return false;
  clearConsentPending(userId, storage);
  return true;
}

/**
 * Whether the held prompt shows on this render (D4, mobile #1972: the phone's prompt flashed
 * back for one render after an answer). An answered prompt that has reached Pick your plan
 * never shows again, whether or not the flag's removal has run yet; until it gets there it
 * stays up, so nothing appears behind it. `answered` is never reset, so there is no render
 * in which the account is owed and not leaving.
 */
export function heldPromptShows({
  answered,
  ...input
}: ConsentHostInput & { answered: boolean }): boolean {
  if (answered && input.pathname === NEW_ACCOUNT_DESTINATION) return false;
  return consentHostDecision(input) === "hold";
}
